import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';

interface SignalRProviderModel {
  hubConnectionState: HubConnectionState;
  connectionId: string | null;
  startListener: (key: string, callback: (...args: any[]) => void) => void;
  stopListener: (key: string, callback: (...args: any[]) => void) => void;
  sendMessage: (key: string, ...args: any[]) => Promise<any>;
}
const defaultSignalRModel: SignalRProviderModel = {
  hubConnectionState: HubConnectionState.Connecting,
  connectionId: null,
  startListener: (key: string, callback: (...args: any[]) => void) => {},
  stopListener: (key: string, callback: (...args: any[]) => void) => {},
  sendMessage: (key: string, ...args: any[]): any => Promise.resolve()
};
const SignalRContext = createContext(defaultSignalRModel);

interface SignalRProviderProps {
  url: string;
  children: ReactNode;
}

export function SignalRProvider(props: SignalRProviderProps) {
  const { children, url } = props;
  const { accessToken, accessTokenPayload } = useOidcAccessToken('config_show_access_token');

  const [hubConnection] = useState<HubConnection>(
    new HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => accessToken
      })
      .withAutomaticReconnect()
      .build()
  );
  const [hubConnectionState, setHubConnectionState] = useState<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  const [connectionId, setConnectionId] = useState<string | null>(null);

  useEffect(() => {
    if (hubConnection.state == HubConnectionState.Disconnected)
      hubConnection
        .start()
        .catch(e =>
          console.info(
            'SignalR Client disconnected.  This may be due to a slow connection or other factors.  Message: ',
            e
          )
        )
        .then(res => {
          setConnectionId(hubConnection.connectionId);
          setHubConnectionState(hubConnection.state);
        });
  }, []);

  hubConnection.onclose(() => setHubConnectionState(hubConnection.state));
  hubConnection.onreconnecting(() => setHubConnectionState(hubConnection.state));
  hubConnection.onreconnected(() => setHubConnectionState(hubConnection.state));

  function startListener(key: string, callback: (...args: any[]) => void) {
    hubConnection.on(key, callback);
  }

  function stopListener(key: string, callback: (...args: any[]) => void) {
    hubConnection.off(key, callback);
  }

  function sendMessage(key: string, ...args: any[]) {
    if (hubConnection.state === HubConnectionState.Connected) {
      return hubConnection.invoke(key, ...args);
    }
    return Promise.resolve();
  }

  const value = {
    hubConnectionState,
    connectionId,
    startListener,
    stopListener,
    sendMessage
  };

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
}

export function useSignalR() {
  return useContext(SignalRContext);
}

export { HubConnectionState };
